import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useStore } from "store";
import { useSearchParams } from "react-router-dom";
import { useWindowDimensions } from "resources/BrowserManager";
import {
  Typography,
  Flex,
  Button,
  Space,
  message,
  Alert,
  Tag,
  Tooltip,
  theme,
} from "antd";
const { Title, Text } = Typography;

import {
  CheckOutlined,
  ClockCircleOutlined,
  ClockCircleFilled,
  CrownFilled,
  GiftFilled,
} from "@ant-design/icons";

import CONSTANTS from "../../../shared/constants";

import ApiManager from "resources/ApiManager/api";

import confetti from "canvas-confetti";

import strings from "resources/strings";

import { getFormattedUnixDate } from "shared/utilities";

import ModalSubscriptionResume from "./ModalSubscriptionResume";
import ModalSubscriptionCancel from "./ModalSubscriptionCancel";
import ModalPaymentMethod from "../../Cuenta/components/ModalPaymentMethod";

import FeatureTag from "components/Shared/FeatureTag";

import "./style.scss";
const styleWrapperClass = "plan";

export default function Plan() {
  const { isSmallView, isMediumView } = useWindowDimensions();

  const [searchParams, setSearchParams] = useSearchParams();
  const newSubscriptionPaymentResult = searchParams.get("r");
  const newSubscriptionNewPlan = searchParams.get("p");

  const { token: theme_token } = theme.useToken();

  const user = useStore((state) => state.user);
  const setUser = useStore((state) => state.setUser);
  const activeRfc = useStore((state) => state.activeRfc);
  const updateUserRfc = useStore((state) => state.updateUserRfc);

  const active_rfc_obj =
    activeRfc !== "nuevo-rfc" ? user.rfcs[activeRfc] : null;

  const userHasPaymentMethod = user.stripe.has_default_payment_method;
  const userHasPreviousOpenInvoices = user.stripe.open_invoices?.length > 0;

  const smallView = isSmallView || isMediumView;

  const showFeedbackSubscripctionUpgradeComplete = () => {
    message.success("Subscripción actualizada.");

    setTimeout(
      () =>
        confetti({
          particleCount: 100,
          spread: 200,
          startVelocity: 25,
          decay: 0.9,
          origin: { x: 0.5, y: 0 },
          angle: 270,
          gravity: 0.8,
          colors: [theme_token.colorPrimary, theme_token.colorSecondary],
        }),
      150,
    );
  };

  async function updateUser() {
    //Get updated user
    const apiRes = await ApiManager.getUser();

    //Silently return when error was for session expired, as the api handler will handle the message and redirection to /login
    if (apiRes.response.code === CONSTANTS.API_RESPONSE_ERROR_SESSION_EXPIRED) {
      return;
    }

    if (apiRes.response.code === -1 || apiRes.response.code !== 2000) {
      message.error(strings.alertUnexpectedError);
      return;
    }

    const { user } = apiRes.data;
    setUser(user);
  }

  const newSubscriptionPaymentDidComplete = async (
    newSubscriptionPaymentResult,
  ) => {
    function sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }

    //Update RFC with the new plan manually so the user sees feedback immediately
    updateUserRfc({
      ...active_rfc_obj,
      plan_name: newSubscriptionNewPlan,
    });

    //Show feedback based on update type
    if (newSubscriptionPaymentResult === "success") {
      message.success("Subscripción actualizada.");
    } else if (newSubscriptionPaymentResult === "upgrade") {
      showFeedbackSubscripctionUpgradeComplete();
    }

    //Wait 10 seconds for update to process with Stripe and then fetch new values
    await sleep(10000);

    //Remove upgrade context from URL (this will trigger the useEffect which will update the user)
    setSearchParams({});
  };

  //Update user when returning from a payment result
  useEffect(() => {
    if (!newSubscriptionPaymentResult) {
      updateUser();
    } else {
      newSubscriptionPaymentDidComplete(newSubscriptionPaymentResult);
    }
  }, [newSubscriptionPaymentResult]);

  const PlanCardActionButton = ({ planObj, currentPlan }) => {
    const actionCancelingPlanResume = (
      <Space direction="vertical">
        <Button
          className="button"
          icon={<ClockCircleOutlined style={{ color: "#C18622" }} />}
          disabled
        >
          Cancelando
        </Button>
        <Text style={{ textAlign: "center" }}>
          <ModalSubscriptionResume rfc={activeRfc} />
        </Text>
      </Space>
    );

    const actionCurrentPlanCancel = (
      <Space direction="vertical">
        <Button className="button" icon={<CheckOutlined />} disabled>
          Plan Actual
        </Button>
        {planObj.key !== "free" && (
          <Text style={{ textAlign: "center" }}>
            <ModalSubscriptionCancel rfc={activeRfc} />
          </Text>
        )}
      </Space>
    );

    const actionNewPlanActivating = (
      <Button className="button" icon={<ClockCircleOutlined />} disabled>
        Activando
      </Button>
    );

    const newSubscriptionPaymentModal = (interactionType) => (
      <ModalPaymentMethod
        interactionType={interactionType}
        rfc={active_rfc_obj.rfc}
        selectedPlan={planObj.key}
        disabled={userHasPreviousOpenInvoices}
        buttonTooltipContent={
          userHasPreviousOpenInvoices
            ? "No puedes cambiar de plan con adeudos pendientes. Por favor, realiza el pago de tus adeudos para continuar."
            : null
        }
        showFeedbackSubscripctionUpgradeComplete={
          showFeedbackSubscripctionUpgradeComplete
        }
      />
    );

    const isCanceling = !!user.stripe.subscription?.cancel_at;

    const ActionContainer = ({ children }) => (
      <Flex justify="center" style={{ marginTop: "20px" }}>
        {children}
      </Flex>
    );

    if (planObj.key === currentPlan) {
      if (isCanceling && currentPlan !== "free") {
        return <ActionContainer>{actionCancelingPlanResume}</ActionContainer>;
      }
      return <ActionContainer>{actionCurrentPlanCancel}</ActionContainer>;
    }

    if (planObj.key === "free") {
      if (isCanceling) {
        return <ActionContainer>{actionNewPlanActivating}</ActionContainer>;
      }
      return (
        <ActionContainer>
          <ModalSubscriptionCancel rfc={activeRfc} type="downgrade" />
        </ActionContainer>
      );
    }

    if (user.stripe.subscription_id) {
      return (
        <ActionContainer>
          {newSubscriptionPaymentModal("updateSubscriptionPayment")}
        </ActionContainer>
      );
    }

    return (
      <ActionContainer>
        {newSubscriptionPaymentModal("newSubscriptionPayment")}
      </ActionContainer>
    );
  };

  const PlanCard = ({ planObj, currentPlan }) => {
    return (
      <div
        className="card"
        style={{
          backgroundColor:
            planObj.key === currentPlan ? "#f9f3f7" : "transparent",
          width: smallView ? "100%" : "30%",
          maxWidth: "370px",
        }}
      >
        <Title level={2} className="title">
          {planObj.planName}
        </Title>
        <Flex justify="center">
          <div
            className="divider"
            style={{ backgroundColor: planObj.color }}
          ></div>
        </Flex>
        <Title level={5} className="description">
          {planObj.description}
        </Title>

        {planObj.key !== "free" &&
        currentPlan === "free" &&
        active_rfc_obj.has_eligible_trial ? (
          <Flex justify="center" style={{ marginTop: "15px" }}>
            <Tooltip
              placement="bottom"
              title="¡Subscríbete y recibe el primer mes de regalo!"
            >
              <Tag
                icon={<GiftFilled />}
                style={{
                  cursor: "help",
                }}
                color="purple"
              >
                ¡Primer mes gratis!
              </Tag>
            </Tooltip>
          </Flex>
        ) : null}

        <Title level={3} className="compare-price">
          {planObj.comparePrice}
        </Title>
        <Title level={3} className="price">
          {planObj.price}
        </Title>

        {planObj.comparePrice ? (
          <Flex justify="center">
            <Tooltip
              placement="bottom"
              title="Aprovecha el precio de lanzamiento por tiempo limitado.*"
            >
              <Tag
                icon={<ClockCircleFilled />}
                style={{
                  cursor: "help",
                }}
                color="purple"
              >
                ¡Precio de lanzamiento!*
              </Tag>
            </Tooltip>
          </Flex>
        ) : null}

        <PlanCardActionButton planObj={planObj} currentPlan={currentPlan} />

        <ul>
          {planObj.features?.map((val, i) => (
            <li key={i}>
              <Space size={5} align="start">
                <CheckOutlined />
                {val}
              </Space>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const featureEFOS = (
    <Text>
      Detección de CFDIs ligados a{" "}
      <Tooltip
        placement="top"
        title={
          <>
            <Text style={{ color: "white" }}>
              Las EFOS son empresas o personas que han sido identificadas por
              las autoridades fiscales como emisoras de facturas falsas o
              simuladas. Estas suelen estar involucradas en actividades
              fraudulentas para evadir impuestos o lavar dinero. Para más
              información, haz click{" "}
              <a
                href={CONSTANTS.SAT_EFOS_INFO_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "white", textDecoration: "underline" }}
              >
                aquí
              </a>
              .
            </Text>
          </>
        }
      >
        <span
          style={{
            textDecoration: "underline dotted",
            cursor: "help",
            color: theme_token.colorPrimary,
          }}
        >
          EFOS
        </span>
      </Tooltip>
      &nbsp;&nbsp;
      <FeatureTag type={"new_feature"} />
    </Text>
  );

  const featureFiscalProfile = (plan) => (
    <Text>
      Perfil Fiscal (actualizaciones {plan === "pro" ? "diarias" : "mensuales"}
      )&nbsp;&nbsp;
      <FeatureTag type={"new_feature"} />
    </Text>
  );

  const planItems = [
    {
      key: "free",
      planName: "Demo",
      color: "red",
      description: "Prueba las funciones principales sin costo.",
      price: "Gratis",
      comparePrice: null,
      features: [
        "Historial de CFDIs del último año",
        "CFDIs recibidos",
        "Información básica de CFDIs",
        "Actualizaciones manuales mensuales de CFDIs",
      ],
    },
    {
      key: "basic",
      planName: "Básico",
      color: "blue",
      description:
        "Ideal para llevar control de facturas recibidas, recibos de nómina, etc.",
      price: "$49/mes",
      comparePrice: "$69/mes",
      features: [
        "Historial de CFDIs de los últimos 2 años",
        "CFDIs recibidos",
        "Información completa de CFDIs",
        "Actualizaciones automáticas semanales de CFDIs",
        "Configuración de alertas por correo electrónico",
        "Visualización de partidas y detalles de CFDIs",
        "Descarga de archivos XML y PDF",
        "Búsqueda básica de CFDIs",
        featureEFOS,
        featureFiscalProfile("basic"),
      ],
    },
    {
      key: "pro",
      planName: (
        <span>
          <CrownFilled className="titleIcon" /> Pro
        </span>
      ),
      color: "gold",
      description: "Ideal para empresas o personas con actividad empresarial.",
      price: "$99/mes",
      comparePrice: "$149/mes",
      features: [
        "Historial de CFDIs de los últimos 5 años",
        "CFDIs recibidos y emitidos",
        "Información completa de CFDIs",
        "Actualizaciones automáticas diarias de CFDIs",
        "Configuración de alertas por correo electrónico",
        "Visualización de partidas y detalles de CFDIs",
        "Descarga de archivos XML y PDF",
        "Búsqueda y filtros avanzados de CFDIs",
        featureEFOS,
        featureFiscalProfile("pro"),
        // "Categorización y clasificación de CFDIs personalizado (etiquetas)",
      ],
    },
  ];

  const PlanChooser = () => {
    //Show the paid plans first in mobile since they show up as columns
    if (smallView) {
      planItems.reverse();
    }

    const cards = planItems.map((item, i) => (
      <PlanCard key={i} planObj={item} currentPlan={active_rfc_obj.plan_name} />
    ));

    const cardsContainer = smallView ? (
      <Flex align="center" gap={30} vertical>
        {cards}
      </Flex>
    ) : (
      <Flex justify="center" gap={30}>
        {cards}
      </Flex>
    );

    return (
      <>
        <Title level={4}>
          Escoge uno de los planes de acuerdo a las funciones que necesites.
        </Title>
        <Flex style={{ marginTop: "30px" }} vertical gap={0}>
          {cardsContainer}
        </Flex>
        <Flex vertical gap={0}>
          <Space size={3}>
            <Text className="footnote">
              Todos los precios en MXN, incluyen IVA.&nbsp;
              {userHasPaymentMethod && <AccountLink />}
            </Text>
          </Space>
          <Text
            className="footnote"
            style={{ marginTop: "5px", marginBottom: "30px" }}
          >
            * Conserva el precio promocional hasta que canceles tu plan.
            Disponible por tiempo limitado.
          </Text>
        </Flex>
      </>
    );
  };

  const AccountLink = () => {
    return (
      <Text className="footnote">
        Para administrar tus métodos de pago, ve a tu&nbsp;
        <Link to="/cuenta" style={{ textDecoration: "underline" }}>
          Cuenta
        </Link>
        .
      </Text>
    );
  };

  const SubscriptionEndsCard = () => {
    return (
      <Alert
        message={
          <Space direction="vertical" style={{ marginLeft: "5px" }}>
            <Text>
              Tu subscripción se cancelará automáticamente el&nbsp;
              <Text strong>
                {getFormattedUnixDate(
                  user.stripe.subscription?.cancel_at,
                  "date",
                )}
              </Text>
              &nbsp;y pasará al plan Demo.
            </Text>
            <Space size={3} wrap>
              <Text>
                Para evitar la cancelación y perder tus beneficios y datos,
                selecciona la opción de
              </Text>
              <ModalSubscriptionResume />
              <Text>en tu plan actual.</Text>
            </Space>
          </Space>
        }
        type="warning"
        showIcon
      />
    );
  };

  return (
    <div className={styleWrapperClass}>
      <Title level={1}>Plan</Title>
      <Flex vertical gap={20}>
        {!!user.stripe.subscription?.cancel_at &&
          active_rfc_obj.plan_name !== "free" && <SubscriptionEndsCard />}
      </Flex>
      <PlanChooser />
    </div>
  );
}
